import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { toast } from 'react-toastify';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as adminActions from '../../actions/admin';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import { t } from '../../i18n';
import './suppliers.scss';

interface SupplierDetailed {
    id: number;
    // The DB column is named 'supplier' (not 'name') — mirrored here so the
    // shape stays consistent with SupplierResource and the KuBIT integration.
    supplier: string;
    articles_count: number;
    can_delete: boolean;
    created_at: string | null;
    updated_at: string | null;
}

interface Props {
    history: any;
    actions: any;
    adminActions: any;
    suppliersDetailed: SupplierDetailed[];
    loading: boolean;
    buttonLoader: boolean;
}

const Suppliers = (props: Props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [search, setSearch] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<SupplierDetailed | null>(null);

    useEffect(() => {
        props.adminActions.getAllSuppliersDetailed();
    }, []);

    const refresh = () => props.adminActions.getAllSuppliersDetailed();

    const list = props.suppliersDetailed || [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(s => (s.supplier || '').toLowerCase().includes(q));
    }, [list, search]);

    const isInitialLoad = props.loading && list.length === 0;

    const handleStartEdit = (supp: SupplierDetailed) => {
        setEditingId(supp.id);
        setEditingName(supp.supplier || '');
    };

    const handleSaveEdit = (supp: SupplierDetailed) => {
        const name = editingName.trim();
        if (!name) {
            toast.error(t('admin.suppliers.toastFillName'));
            return;
        }
        if (name === supp.supplier) {
            setEditingId(null);
            return;
        }
        // editSupplier expects { supplier: <value> } per SupplierService.updateSupplier
        props.adminActions.editSupplier(supp.id, { supplier: name }).then(() => {
            setEditingId(null);
            setEditingName('');
            refresh();
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleDelete = (supp: SupplierDetailed) => {
        if (!supp.can_delete) {
            toast.error(t('admin.suppliers.toast.hasArticles'));
            return;
        }
        setDeleteConfirm(supp);
    };

    const confirmDelete = () => {
        if (!deleteConfirm) return;
        const id = deleteConfirm.id;
        props.adminActions.deleteSupplier(id).then(() => {
            setDeleteConfirm(null);
            refresh();
        });
    };

    const handleStartCreate = () => {
        setCreating(true);
        setNewName('');
    };

    const handleSaveCreate = () => {
        const name = newName.trim();
        if (!name) {
            toast.error(t('admin.suppliers.toastFillName'));
            return;
        }
        // saveSupplier expects { supplier: <value> } per SupplierService.addSupplier
        props.adminActions.saveSupplier({ supplier: name }).then(() => {
            setCreating(false);
            setNewName('');
            refresh();
        });
    };

    const handleCancelCreate = () => {
        setCreating(false);
        setNewName('');
    };

    const renderCheckIcon = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );

    const renderCloseIcon = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    return (
        <div className="supp-page">
            <MikaPageHeader
                title={<FormattedMessage id="admin.suppliers.pageTitle" defaultMessage="Furnitorët" />}
                subtitle={fm('admin.suppliers.pageSubtitle', 'Menaxho furnitorët dhe artikujt që furnizojnë')}
                actions={
                    <button
                        type="button"
                        className="supp-btn supp-btn--primary"
                        onClick={handleStartCreate}
                        disabled={creating || props.buttonLoader}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5v14" />
                        </svg>
                        <FormattedMessage id="admin.suppliers.createBtn" defaultMessage="Krijo furnitor" />
                    </button>
                }
            />

            <div className="supp-toolbar">
                <div className="supp-search">
                    <svg className="supp-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        placeholder={fm('admin.suppliers.searchPlaceholder', 'Kërko furnitor...')}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className="supp-toolbar__count">
                    <FormattedMessage
                        id="admin.suppliers.countTotal"
                        defaultMessage="{count, plural, one {# furnitor} other {# furnitorë}}"
                        values={{ count: filtered.length }}
                    />
                </div>
            </div>

            <div className="supp-list">
                <div className="supp-list__header">
                    <div className="supp-list__col supp-list__col--name">
                        <FormattedMessage id="admin.suppliers.col.name" defaultMessage="Furnitori" />
                    </div>
                    <div className="supp-list__col supp-list__col--num">
                        <FormattedMessage id="admin.suppliers.col.articles" defaultMessage="Artikuj" />
                    </div>
                    <div className="supp-list__col supp-list__col--actions" />
                </div>

                {isInitialLoad ? (
                    [0, 1, 2, 3].map(i => (
                        <div key={`sk-${i}`} className="supp-row supp-row--skeleton">
                            <div className="supp-row__cell supp-row__cell--name">
                                <div className="supp-skeleton supp-skeleton--icon" />
                                <div className="supp-skeleton supp-skeleton--name" />
                            </div>
                            <div className="supp-row__cell supp-row__cell--num"><div className="supp-skeleton supp-skeleton--num" /></div>
                            <div className="supp-row__cell supp-row__cell--actions" />
                        </div>
                    ))
                ) : (
                    <>
                        {filtered.length === 0 && !creating && (
                            <div className="supp-empty">
                                {search.trim() ? (
                                    <FormattedMessage id="admin.suppliers.empty.search" defaultMessage="Asnjë furnitor nuk përputhet me kërkimin." />
                                ) : (
                                    <FormattedMessage id="admin.suppliers.empty.list" defaultMessage="Nuk ka furnitorë. Krijoni një të ri për të filluar." />
                                )}
                            </div>
                        )}

                        {filtered.map(supp => {
                            const isEditing = editingId === supp.id;
                            const initial = ((supp.supplier || '?').charAt(0) || '?').toUpperCase();

                            return (
                                <div
                                    key={supp.id}
                                    className={`supp-row ${isEditing ? 'supp-row--editing' : ''}`}
                                >
                                    <div className="supp-row__cell supp-row__cell--name">
                                        <div className="supp-row__icon">{initial}</div>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                className="supp-row__edit-input"
                                                value={editingName}
                                                onChange={e => setEditingName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSaveEdit(supp);
                                                    }
                                                    if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        handleCancelEdit();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            <div className="supp-row__name">{supp.supplier || '—'}</div>
                                        )}
                                    </div>
                                    <div className="supp-row__cell supp-row__cell--num">{supp.articles_count}</div>
                                    <div className="supp-row__cell supp-row__cell--actions">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="supp-icon-btn supp-icon-btn--success"
                                                    onClick={() => handleSaveEdit(supp)}
                                                    title={fm('common.action.save', 'Ruaj')}
                                                    disabled={props.buttonLoader}
                                                >
                                                    {renderCheckIcon}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="supp-icon-btn"
                                                    onClick={handleCancelEdit}
                                                    title={fm('common.action.cancel', 'Anulo')}
                                                >
                                                    {renderCloseIcon}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="supp-icon-btn"
                                                    onClick={() => handleStartEdit(supp)}
                                                    title={fm('common.action.edit', 'Ndrysho')}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="supp-icon-btn supp-icon-btn--danger"
                                                    onClick={() => handleDelete(supp)}
                                                    title={supp.can_delete ? fm('common.action.delete', 'Fshi') : fm('admin.suppliers.toast.hasArticles', 'Nuk mund ta fshish — ka artikuj të lidhur')}
                                                    disabled={!supp.can_delete}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                    </svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {creating ? (
                            <div className="supp-add-row supp-add-row--editing">
                                <div className="supp-row__cell supp-row__cell--name">
                                    <div className="supp-row__icon supp-row__icon--add">+</div>
                                    <input
                                        type="text"
                                        className="supp-row__edit-input"
                                        value={newName}
                                        placeholder={fm('admin.suppliers.placeholderName', 'Emri i furnitorit...')}
                                        onChange={e => setNewName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSaveCreate();
                                            }
                                            if (e.key === 'Escape') {
                                                e.preventDefault();
                                                handleCancelCreate();
                                            }
                                        }}
                                        autoFocus
                                    />
                                </div>
                                <div className="supp-row__cell supp-row__cell--actions">
                                    <button
                                        type="button"
                                        className="supp-icon-btn supp-icon-btn--success"
                                        onClick={handleSaveCreate}
                                        title={fm('common.action.create', 'Krijo')}
                                        disabled={props.buttonLoader}
                                    >
                                        {renderCheckIcon}
                                    </button>
                                    <button
                                        type="button"
                                        className="supp-icon-btn"
                                        onClick={handleCancelCreate}
                                        title={fm('common.action.cancel', 'Anulo')}
                                    >
                                        {renderCloseIcon}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="supp-add-row" onClick={handleStartCreate}>
                                <div className="supp-row__icon supp-row__icon--add">+</div>
                                <div className="supp-add-row__label">
                                    <FormattedMessage id="admin.suppliers.addNew" defaultMessage="Krijo furnitor të ri" />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <MikaConfirmModal
                isOpen={deleteConfirm !== null}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={confirmDelete}
                title={fm('admin.suppliers.deleteTitle', 'Fshi furnitorin')}
                message={intl.formatMessage(
                    { id: 'admin.suppliers.deleteConfirm', defaultMessage: 'A jeni të sigurt që doni të fshini {name}?' },
                    { name: '"' + (deleteConfirm?.supplier || '') + '"' }
                )}
                confirmLabel={fm('common.action.delete', 'Fshi')}
                confirmColor="danger"
            />
        </div>
    );
};

function mapGlobalStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        loading: state.app.loading,
        buttonLoader: state.app.buttonLoader,
        suppliersDetailed: (state.admin as any).suppliersDetailed || [],
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapGlobalStateToProps, mapDispatchToProps)(Suppliers as any));
